import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { Input } from '../input';
import { cn } from '../../lib/utils';
import { FieldLabelRow } from './field-label-row';

export default function NumberField({
	label,
	className,
	inputClassName,
	optional,
	info,
	...props
}: ComponentProps<'input'> & {
	label: string;
	inputClassName?: string;
	optional?: boolean;
	info?: ReactNode;
}) {
	const field = useFieldContext<number | null | undefined>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);

	return (
		<div className={className}>
			<div className="grid gap-2">
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
					/>
				)}
				<Input
					type={isReadOnly ? 'text' : 'number'}
					id={field.name}
					name={field.name}
					aria-invalid={hasFieldError(field.state.meta)}
					value={isReadOnly && (field.state.value == null || field.state.value === 0) ? '-' : (field.state.value ?? '')}
					onBlur={field.handleBlur}
					onChange={(e) => field.handleChange(e.target.value === '' ? null : Number(e.target.value))}
					{...props}
					readOnly={isReadOnly}
					tabIndex={isReadOnly ? -1 : undefined}
					className={cn(
						inputClassName,
						isReadOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
					)}
				/>
			</div>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
